import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid2 as Grid,
  Paper,
  IconButton,
  FormControlLabel,
  Checkbox,
  Alert,
  useMediaQuery,
  useTheme,
  Fab,
  Zoom,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Visibility,
  Add,
  FilterList,
  ViewList,
  ViewModule,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  loadInspectionTemplates,
  transitionInspection,
} from "../../../../store/actions/ppActions";
import { InspectionStatusChip } from "../components/StatusChips";

const PpInspectionsBoardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [inspections, setInspections] = useState([]);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [actionType, setActionType] = useState("");
  const [actionData, setActionData] = useState({
    comment: "",
    targetDate: "",
    isPartial: false,
  });
  const [loading, setLoading] = useState(false);

  // New filtering and sorting state
  const [statusFilter, setStatusFilter] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState("");
  const [contractorFilter, setContractorFilter] = useState("");
  const [sortBy, setSortBy] = useState("urgency"); // urgency, date, contractor
  const [viewMode, setViewMode] = useState(isMobile ? "list" : "kanban");
  const [filtersVisible, setFiltersVisible] = useState(false);

  useEffect(() => {
    // Enhanced mock inspection data with urgency and additional info
    const mockInspections = [
      {
        id: "INS001",
        permitId: "PKG2024001",
        name: "Footing/Foundation",
        address: "123 Oak Street, Jacksonville, FL 32207",
        contractor: "ABC Construction",
        status: "pending",
        requestedDate: new Date(
          Date.now() + 2 * 24 * 60 * 60 * 1000
        ).toISOString(),
        submittedDate: new Date(
          Date.now() - 1 * 24 * 60 * 60 * 1000
        ).toISOString(),
        scheduledDate: null,
        dueDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(),
        urgency: "high", // high, medium, low
        type: "required",
        permitType: "Building",
        rushTier: "express",
        dependencies: [],
        photos: [],
        comments: "",
        city: "Duval County",
      },
      {
        id: "INS002",
        permitId: "PKG2024001",
        name: "Framing",
        address: "123 Oak Street, Jacksonville, FL 32207",
        contractor: "ABC Construction",
        status: "pre_approved",
        requestedDate: new Date(
          Date.now() + 5 * 24 * 60 * 60 * 1000
        ).toISOString(),
        submittedDate: new Date(
          Date.now() - 2 * 24 * 60 * 60 * 1000
        ).toISOString(),
        scheduledDate: new Date(
          Date.now() + 5 * 24 * 60 * 60 * 1000
        ).toISOString(),
        dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        urgency: "medium",
        type: "required",
        permitType: "Building",
        rushTier: "standard",
        dependencies: ["INS001"],
        photos: ["photo1.jpg", "photo2.jpg"],
        comments: "Pre-approved pending foundation completion",
        city: "Duval County",
      },
      {
        id: "INS003",
        permitId: "PKG2024002",
        name: "Final Commercial",
        address: "456 Pine Avenue, St Augustine, FL 32084",
        contractor: "Best Builders LLC",
        status: "approved",
        requestedDate: new Date(
          Date.now() + 1 * 24 * 60 * 60 * 1000
        ).toISOString(),
        submittedDate: new Date(
          Date.now() - 3 * 24 * 60 * 60 * 1000
        ).toISOString(),
        scheduledDate: new Date(
          Date.now() + 1 * 24 * 60 * 60 * 1000
        ).toISOString(),
        dueDate: new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString(), // Due in 6 hours
        urgency: "high",
        type: "required",
        permitType: "Commercial",
        rushTier: "express",
        dependencies: [],
        photos: ["final1.jpg", "final2.jpg", "final3.jpg"],
        comments: "Ready for final inspection",
        city: "St Johns County",
      },
      {
        id: "INS004",
        permitId: "PKG2024003",
        name: "Electrical Rough-In",
        address: "789 Maple Drive, Green Cove Springs, FL 32043",
        contractor: "Prime Contracting",
        status: "failed",
        requestedDate: new Date(
          Date.now() - 1 * 24 * 60 * 60 * 1000
        ).toISOString(),
        submittedDate: new Date(
          Date.now() - 5 * 24 * 60 * 60 * 1000
        ).toISOString(),
        scheduledDate: new Date(
          Date.now() - 1 * 24 * 60 * 60 * 1000
        ).toISOString(),
        dueDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // Overdue
        urgency: "high",
        type: "required",
        permitType: "Electrical",
        rushTier: "standard",
        dependencies: [],
        photos: [],
        comments: "Failed - GFCI outlets not properly installed per NEC 210.8",
        failureReason: "NEC 210.8 - GFCI outlet requirements not met",
        city: "Clay County",
      },
      {
        id: "INS005",
        permitId: "PKG2024004",
        name: "Mechanical Rough-In",
        address: "321 Cedar Lane, Fernandina Beach, FL 32034",
        contractor: "HVAC Specialists",
        status: "pending",
        requestedDate: new Date(
          Date.now() + 7 * 24 * 60 * 60 * 1000
        ).toISOString(),
        submittedDate: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        scheduledDate: null,
        dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
        urgency: "low",
        type: "required",
        permitType: "Mechanical",
        rushTier: "standard",
        dependencies: [],
        photos: ["mech1.jpg"],
        comments: "",
        city: "Nassau County",
      },
    ];

    setInspections(mockInspections);
    dispatch(loadInspectionTemplates());
  }, [dispatch]);

  // Filtering and sorting logic
  const getFilteredAndSortedInspections = () => {
    let filtered = [...inspections];

    // Apply filters
    if (statusFilter) {
      filtered = filtered.filter(
        (inspection) => inspection.status === statusFilter
      );
    }

    if (urgencyFilter) {
      filtered = filtered.filter(
        (inspection) => inspection.urgency === urgencyFilter
      );
    }

    if (contractorFilter) {
      filtered = filtered.filter((inspection) =>
        inspection.contractor
          .toLowerCase()
          .includes(contractorFilter.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "urgency":
          // Sort by urgency first, then by due date
          const urgencyOrder = { high: 3, medium: 2, low: 1 };
          const urgencyDiff = urgencyOrder[b.urgency] - urgencyOrder[a.urgency];
          if (urgencyDiff !== 0) return urgencyDiff;
          return new Date(a.dueDate) - new Date(b.dueDate);

        case "date":
          return new Date(a.submittedDate) - new Date(b.submittedDate);

        case "contractor":
          return a.contractor.localeCompare(b.contractor);

        default:
          return 0;
      }
    });

    return filtered;
  };

  const getUrgencyChip = (urgency, dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const hoursUntilDue = (due - now) / (1000 * 60 * 60);

    let color = "default";
    let label = urgency;

    if (hoursUntilDue < 0) {
      color = "error";
      label = "OVERDUE";
    } else if (hoursUntilDue < 6) {
      color = "error";
      label = "URGENT";
    } else if (hoursUntilDue < 24) {
      color = "warning";
      label = "HIGH";
    } else if (urgency === "high") {
      color = "warning";
    } else if (urgency === "medium") {
      color = "info";
    } else {
      color = "default";
    }

    return <Chip label={label.toUpperCase()} color={color} size="small" />;
  };

  const formatTimeUntilDue = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due - now;

    if (diffMs < 0) {
      const hoursOverdue = Math.floor(Math.abs(diffMs) / (1000 * 60 * 60));
      return `${hoursOverdue}h overdue`;
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (hours < 24) {
      return `${hours}h left`;
    }

    const days = Math.floor(hours / 24);
    return `${days}d left`;
  };

  const handleInspectionAction = async () => {
    if (!selectedInspection || !actionType) return;

    try {
      setLoading(true);
      await dispatch(
        transitionInspection(
          selectedInspection.permitId,
          selectedInspection.id,
          actionType,
          actionData
        )
      );

      // Update local state
      setInspections((prev) =>
        prev.map((inspection) =>
          inspection.id === selectedInspection.id
            ? {
                ...inspection,
                status: getNewStatus(actionType),
                comments: actionData.comment || inspection.comments,
                scheduledDate:
                  actionData.targetDate || inspection.scheduledDate,
              }
            : inspection
        )
      );

      setActionDialogOpen(false);
      resetActionState();
    } catch (error) {
      console.error("Failed to update inspection:", error);
    } finally {
      setLoading(false);
    }
  };

  const getNewStatus = (action) => {
    const statusMap = {
      pre_approve: "pre_approved",
      approve: "approved",
      report: "reported",
      pass: "passed",
      fail: "failed",
      request_corrections: "corrections_needed",
    };
    return statusMap[action] || "pending";
  };

  const resetActionState = () => {
    setSelectedInspection(null);
    setActionType("");
    setActionData({ comment: "", targetDate: "", isPartial: false });
  };

  const openActionDialog = (inspection, action) => {
    setSelectedInspection(inspection);
    setActionType(action);
    setActionDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const canTransition = (inspection, action) => {
    // Basic dependency checking
    if (action === "approve" && inspection.dependencies.length > 0) {
      const dependentInspections = inspections.filter(
        (i) => inspection.dependencies.includes(i.id) && i.status !== "passed"
      );
      return dependentInspections.length === 0;
    }
    return true;
  };

  const InspectionCard = ({ inspection, showCompact = false }) => (
    <Card
      sx={{
        mb: 2,
        mx: 0.5, // Add horizontal margin for glow effect
        border: inspection.urgency === "high" ? 2 : 1,
        borderColor:
          inspection.urgency === "high"
            ? "error.main"
            : inspection.type === "required"
              ? "primary.main"
              : "grey.300",
        ...(inspection.urgency === "high" && {
          animation: "pulse 2s infinite",
          "@keyframes pulse": {
            "0%": { boxShadow: "0 0 0 0 rgba(244, 67, 54, 0.7)" },
            "70%": { boxShadow: "0 0 0 10px rgba(244, 67, 54, 0)" },
            "100%": { boxShadow: "0 0 0 0 rgba(244, 67, 54, 0)" },
          },
        }),
        // Allow card to grow with content but maintain reasonable constraints
        width: "100%",
        maxWidth: "400px", // Set a reasonable max width
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ pb: showCompact ? 2 : 1, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
            gap: 1, // Add gap to prevent overlap
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: "hidden" }}>
            <Typography
              variant={showCompact ? "body1" : "subtitle1"}
              fontWeight="bold"
              sx={{
                wordBreak: "break-word",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {inspection.name}
            </Typography>
            {!showCompact && (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    mt: 0.5,
                  }}
                >
                  {inspection.address}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                    mt: 0.5,
                  }}
                >
                  {inspection.contractor} • {inspection.permitId}
                </Typography>
              </>
            )}
            {showCompact && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  wordBreak: "break-word",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  mt: 0.5,
                }}
              >
                {inspection.contractor} • {inspection.city}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.5,
              flexShrink: 0, // Prevent shrinking
              minWidth: "fit-content",
            }}
          >
            <InspectionStatusChip status={inspection.status} />
            {getUrgencyChip(inspection.urgency, inspection.dueDate)}
            {inspection.type === "required" && (
              <Chip
                label="Required"
                color="primary"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        {/* Due Date with urgency indicator */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            flexWrap: "wrap",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              wordBreak: "break-word",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            Due: {formatTimeUntilDue(inspection.dueDate)}
          </Typography>
          <Chip
            label={inspection.rushTier}
            color={inspection.rushTier === "express" ? "error" : "default"}
            size="small"
            variant="outlined"
            sx={{ flexShrink: 0 }}
          />
        </Box>

        {!showCompact && (
          <>
            {inspection.requestedDate && (
              <Typography variant="caption" color="text.secondary">
                Requested: {formatDate(inspection.requestedDate)}
              </Typography>
            )}

            {inspection.scheduledDate && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ display: "block" }}
              >
                Scheduled: {formatDate(inspection.scheduledDate)}
              </Typography>
            )}

            {inspection.photos && inspection.photos.length > 0 && (
              <Typography
                variant="caption"
                color="success.main"
                sx={{ display: "block" }}
              >
                {inspection.photos.length} photos uploaded
              </Typography>
            )}

            {inspection.comments && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  p: 1,
                  bgcolor: "grey.50",
                  borderRadius: 1,
                  wordBreak: "break-word",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  maxHeight: "4.5em", // Approximately 3 lines
                }}
              >
                {inspection.comments}
              </Typography>
            )}

            {inspection.failureReason && (
              <Alert
                severity="error"
                sx={{
                  mt: 1,
                  "& .MuiAlert-message": {
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  },
                }}
              >
                {inspection.failureReason}
              </Alert>
            )}
          </>
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            mt: 2,
            flexWrap: "wrap",
            justifyContent: "flex-start",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {inspection.status === "pending" && (
            <>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => openActionDialog(inspection, "pre_approve")}
                disabled={!canTransition(inspection, "pre_approve")}
                sx={{
                  minWidth: "fit-content",
                  fontSize: "0.75rem",
                  px: 1,
                }}
              >
                Pre-Approve
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => openActionDialog(inspection, "approve")}
                disabled={!canTransition(inspection, "approve")}
                sx={{
                  minWidth: "fit-content",
                  fontSize: "0.75rem",
                  px: 1,
                }}
              >
                Approve
              </Button>
            </>
          )}

          {inspection.status === "pre_approved" && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => openActionDialog(inspection, "approve")}
              sx={{
                minWidth: "fit-content",
                fontSize: "0.75rem",
                px: 1,
              }}
            >
              Final Approve
            </Button>
          )}

          {inspection.status === "approved" && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={() => openActionDialog(inspection, "report")}
              sx={{
                minWidth: "fit-content",
                fontSize: "0.75rem",
                px: 1,
              }}
            >
              Report to City
            </Button>
          )}

          {inspection.status === "reported" && (
            <>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => openActionDialog(inspection, "pass")}
                sx={{
                  minWidth: "fit-content",
                  fontSize: "0.75rem",
                  px: 1,
                }}
              >
                Mark Passed
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => openActionDialog(inspection, "fail")}
                sx={{
                  minWidth: "fit-content",
                  fontSize: "0.75rem",
                  px: 1,
                }}
              >
                Mark Failed
              </Button>
            </>
          )}

          {(inspection.status === "failed" ||
            inspection.status === "corrections_needed") && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() =>
                openActionDialog(inspection, "request_corrections")
              }
              sx={{
                minWidth: "fit-content",
                fontSize: "0.75rem",
                px: 1,
              }}
            >
              Request Corrections
            </Button>
          )}

          <IconButton
            size="small"
            onClick={() => navigate(`/pp/inspections/${inspection.permitId}`)}
            sx={{
              flexShrink: 0,
              ml: "auto",
            }}
          >
            <Visibility />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  const columns = [
    { id: "pending", title: "Pending", color: "default" },
    { id: "pre_approved", title: "Pre-Approved", color: "warning" },
    { id: "approved", title: "Approved", color: "primary" },
    { id: "reported", title: "Reported", color: "secondary" },
    { id: "passed", title: "Passed", color: "success" },
    { id: "failed", title: "Failed", color: "error" },
  ];

  const filteredInspections = getFilteredAndSortedInspections();

  const renderKanbanView = () => (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        overflowX: "auto",
        overflowY: "hidden",
        minHeight: "60vh",
        px: 1,
        width: "100%",
        "&::-webkit-scrollbar": {
          height: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(0,0,0,0.1)",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0,0,0,0.3)",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "rgba(0,0,0,0.5)",
        },
      }}
    >
      {columns.map((column) => {
        const columnInspections = filteredInspections.filter(
          (i) => i.status === column.id
        );
        return (
          <Paper
            key={column.id}
            sx={{
              minWidth: isMobile ? 280 : 300,
              width: "auto", // Let it grow based on content
              p: 2,
              bgcolor: "grey.50",
              borderTop: 4,
              borderColor: `${column.color}.main`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="h6"
                color={`${column.color}.main`}
                sx={{
                  wordBreak: "break-word",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {column.title}
              </Typography>
              <Chip
                label={columnInspections.length}
                color={column.color}
                size="small"
                variant="outlined"
                sx={{ flexShrink: 0, ml: 1 }}
              />
            </Box>

            <Box
              sx={{
                maxHeight: "60vh",
                overflowY: "auto",
                overflowX: "hidden",
                px: 1,
                pt: 1,
                flex: 1,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(0,0,0,0.05)",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "rgba(0,0,0,0.3)",
                },
              }}
            >
              {columnInspections.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ py: 4 }}
                >
                  No inspections
                </Typography>
              ) : (
                columnInspections.map((inspection) => (
                  <InspectionCard
                    key={inspection.id}
                    inspection={inspection}
                    showCompact={isMobile}
                  />
                ))
              )}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );

  const renderListView = () => (
    <Grid container spacing={2}>
      {filteredInspections.length === 0 ? (
        <Grid size={{ xs: 12 }}>
          <Alert severity="info">
            No inspections match the current filters.
          </Alert>
        </Grid>
      ) : (
        filteredInspections.map((inspection) => (
          <Grid key={inspection.id} size={{ xs: 12, md: 6, lg: 4 }}>
            <InspectionCard inspection={inspection} showCompact={false} />
          </Grid>
        ))
      )}
    </Grid>
  );

  return (
    <Box sx={{ p: isMobile ? 2 : 3, pb: isMobile ? 10 : 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography color="primary" variant="h4">
          Inspections Board
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="kanban">
              <ViewModule />
            </ToggleButton>
            <ToggleButton value="list">
              <ViewList />
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFiltersVisible(!filtersVisible)}
          >
            {isMobile ? "Filter" : "Filters"}
          </Button>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/pp/inspections/templates")}
          >
            {isMobile ? "Templates" : "Manage Templates"}
          </Button>
        </Box>
      </Box>

      {/* Filters Panel */}
      {filtersVisible && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="pre_approved">Pre-Approved</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="reported">Reported</MenuItem>
                    <MenuItem value="passed">Passed</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Urgency</InputLabel>
                  <Select
                    value={urgencyFilter}
                    label="Urgency"
                    onChange={(e) => setUrgencyFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Contractor"
                  value={contractorFilter}
                  onChange={(e) => setContractorFilter(e.target.value)}
                  placeholder="Filter by contractor..."
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    label="Sort By"
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <MenuItem value="urgency">Urgency</MenuItem>
                    <MenuItem value="date">Submit Date</MenuItem>
                    <MenuItem value="contractor">Contractor</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setStatusFilter("");
                    setUrgencyFilter("");
                    setContractorFilter("");
                    setSortBy("urgency");
                  }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h6">{filteredInspections.length}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="caption" color="text.secondary">
                High Urgency
              </Typography>
              <Typography variant="h6" color="error.main">
                {filteredInspections.filter((i) => i.urgency === "high").length}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Pending
              </Typography>
              <Typography variant="h6" color="warning.main">
                {
                  filteredInspections.filter((i) => i.status === "pending")
                    .length
                }
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Overdue
              </Typography>
              <Typography variant="h6" color="error.main">
                {
                  filteredInspections.filter(
                    (i) => new Date(i.dueDate) < new Date()
                  ).length
                }
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Content */}
      {viewMode === "kanban" ? renderKanbanView() : renderListView()}

      {/* Mobile FAB for Quick Actions */}
      {isMobile && (
        <Zoom in={true}>
          <Fab
            color="primary"
            sx={{ position: "fixed", bottom: 80, right: 16 }}
            onClick={() => setFiltersVisible(!filtersVisible)}
          >
            <FilterList />
          </Fab>
        </Zoom>
      )}

      {/* Action Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionType === "pre_approve" && "Pre-Approve Inspection"}
          {actionType === "approve" && "Approve Inspection"}
          {actionType === "report" && "Report to City"}
          {actionType === "pass" && "Mark as Passed"}
          {actionType === "fail" && "Mark as Failed"}
          {actionType === "request_corrections" && "Request Corrections"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedInspection?.name} - {selectedInspection?.address}
          </Typography>

          {(actionType === "approve" || actionType === "pre_approve") && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Target Date"
                  type="datetime-local"
                  value={actionData.targetDate}
                  onChange={(e) =>
                    setActionData((prev) => ({
                      ...prev,
                      targetDate: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={actionData.isPartial}
                      onChange={(e) =>
                        setActionData((prev) => ({
                          ...prev,
                          isPartial: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Partial approval (requires follow-up)"
                />
              </Grid>
            </Grid>
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            label={
              actionType === "fail" ? "Failure Reason (Required)" : "Comments"
            }
            value={actionData.comment}
            onChange={(e) =>
              setActionData((prev) => ({ ...prev, comment: e.target.value }))
            }
            required={actionType === "fail"}
            sx={{ mt: 2 }}
            placeholder={
              actionType === "fail"
                ? "Specify building code section and reason for failure..."
                : "Add any comments or notes..."
            }
          />

          {actionType === "fail" && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Required:</strong> You must cite the specific building
              code section that this inspection failed to meet (e.g., IBC
              1805.4.2, IRC R502.3.1)
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleInspectionAction}
            disabled={
              loading || (actionType === "fail" && !actionData.comment.trim())
            }
            color={
              actionType === "pass"
                ? "success"
                : actionType === "fail"
                  ? "error"
                  : actionType === "approve"
                    ? "primary"
                    : "warning"
            }
          >
            {loading
              ? "Processing..."
              : actionType === "pre_approve"
                ? "Pre-Approve"
                : actionType === "approve"
                  ? "Approve"
                  : actionType === "report"
                    ? "Report to City"
                    : actionType === "pass"
                      ? "Mark Passed"
                      : actionType === "fail"
                        ? "Mark Failed"
                        : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PpInspectionsBoardPage;
